/**
 * JST Sections Configuration
 * Defines the field structure, dropdown options, and table columns for each JST section.
 * This single config drives the reusable JSTInternal/JSTForm components.
 */

import { RANKS_BY_BRANCH, CLEP_TITLES, DLPT_LANGUAGE_OPTIONS } from 'constants/militaryOptions';
import { EDUCATION_CREDENTIAL_ITEMS } from 'constants/educationLevelOptions';

const EDUCATION_TYPE_OPTIONS: FieldOption[] = EDUCATION_CREDENTIAL_ITEMS.map((item) => ({
  value: item.key,
  labelId: item.labelId,
}));

export interface FieldOption {
  value: string;
  label?: string;
  labelId?: string;
}

export interface FieldConfig {
  name: string;
  label: string;
  labelId?: string;
  type:
    | 'text'
    | 'select'
    | 'date'
    | 'number'
    | 'textarea'
    | 'button'
    | 'checkbox'
    | 'skills'
    | 'spacer'
    | 'autocomplete'
    | 'militaryTitleAutocomplete'
    | 'militaryOccupationSelect'
    | 'country-select'
    | 'state-select'
    | 'hidden';
  options?: FieldOption[];
  required?: boolean;
  gridSize?: number; // MUI grid xs size (1-12), default 6
  disablesField?: string; // when checkbox is checked, disable this field
  maxLength?: number;
  minLength?: number;
  pattern?: RegExp;
  patternMessage?: string;
  dependsOn?: {
    field: string; // name of the parent field this depends on
    optionsMap: Record<string, FieldOption[]>; // parent value → child options
  };
  subFields?: FieldConfig[]; // nested form fields for type='button'
  displayField?: string; // sub-field name whose values populate the parent field after adding
  groupLabel?: string; // groups consecutive fields under a common header in the form
  autoPopulatesSkillsField?: string; // on blur, auto-match words in this field against skills and populate the named skills field
  autoPopulatesOccupationField?: string; // on select, populate the named field with the selected option's MOC code
  hideIfFieldValue?: { field: string; value: string | string[] }; // hide this field when the specified field has the specified value
  mocOccupationsFrom?: string; // for type='militaryOccupationSelect': name of the militaryTitleAutocomplete field whose selected MOC supplies the occupation options
}

export interface SubSectionConfig {
  id: string;
  subtitle: string;
  subtitleId?: string;
  singularTitle?: string;
  singularTitleId?: string;
  buttonLabel: string;
  buttonLabelId?: string;
  fields: FieldConfig[];
  summaryColumns?: string[];
  defaultValues?: Record<string, string>;
}

export interface SectionConfig {
  id: string;
  title: string;
  titleId?: string;
  singularTitle?: string;
  singularTitleId?: string;
  subtitle?: string;
  subtitleId?: string;
  note?: string;
  noteId?: string;
  buttonLabel?: string;
  buttonLabelId?: string;
  fields?: FieldConfig[];
  subSections?: SubSectionConfig[];
  summaryColumns?: string[];
}

// --- JST-specific Dropdown Options (values match API enums) ---

/** Convert label to lowerCamelCase for API enum values */
const toCamelCase = (label: string): string => {
  let str = label.replace(/\s*\([^)]*\)/g, '').trim();
  str = str
    .replace(/[^a-zA-Z0-9 ]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
  const words = str.split(' ');
  return words
    .map((w, i) => {
      if (/^\d+$/.test(w)) return w;
      if (/^[IVX]+$/i.test(w) && w.length <= 3) return w.toUpperCase();
      return i === 0 ? w.toLowerCase() : w.charAt(0).toUpperCase() + w.slice(1).toLowerCase();
    })
    .join('');
};

// personal.branch: API expects camelCase ('airForce', 'coastGuard', etc.)
const branchOptions: FieldOption[] = [
  { value: 'airForce', label: 'Air Force' },
  { value: 'airNationalGuard', label: 'Air National Guard' },
  { value: 'army', label: 'Army' },
  { value: 'armyNationalGuard', label: 'Army National Guard' },
  { value: 'coastGuard', label: 'Coast Guard' },
  { value: 'marineCorps', label: 'Marine Corps' },
  { value: 'navy', label: 'Navy' },
  { value: 'spaceForce', label: 'Space Force' },
];

// military_experience.branch_of_service: API expects display names ('Air Force', 'Coast Guard', etc.)
const militaryExpBranchOptions: FieldOption[] = [
  { value: 'Air Force', label: 'Air Force' },
  { value: 'Air National Guard', label: 'Air National Guard' },
  { value: 'Army', label: 'Army' },
  { value: 'Army National Guard', label: 'Army National Guard' },
  { value: 'Coast Guard', label: 'Coast Guard' },
  { value: 'Marine Corps', label: 'Marine Corps' },
  { value: 'Navy', label: 'Navy' },
  { value: 'Space Force', label: 'Space Force' },
];

/** All rank values in militaryOptions.js match backend enums exactly — pass through as-is */
const toCamelCaseRanks = (ranks: FieldOption[]): FieldOption[] => ranks;

// Ranks keyed by camelCase branch values (matching personal.branchOptions values)
export const jstRanksByBranch: Record<string, FieldOption[]> = {
  army: toCamelCaseRanks(RANKS_BY_BRANCH.army),
  coastGuard: toCamelCaseRanks(RANKS_BY_BRANCH.coastguard),
  airNationalGuard: toCamelCaseRanks(RANKS_BY_BRANCH.airnationalguard),
  armyNationalGuard: toCamelCaseRanks(RANKS_BY_BRANCH.armynationalguard),
  spaceForce: toCamelCaseRanks(RANKS_BY_BRANCH.spaceforce),
  navy: toCamelCaseRanks(RANKS_BY_BRANCH.navy),
  airForce: toCamelCaseRanks(RANKS_BY_BRANCH.airforce),
  marineCorps: toCamelCaseRanks(RANKS_BY_BRANCH.marinecorps),
};

// Combined unique ranks as fallback when no branch is selected yet
export const jstRankOptions: FieldOption[] = Object.values(jstRanksByBranch)
  .flat()
  .filter((r, i, arr) => arr.findIndex((x) => x.value === r.value) === i);

// Keep local alias for use within this file
const rankOptions = jstRankOptions;

// Ranks keyed by display-name branch values (matching militaryExpBranchOptions values)
const militaryExpRanksByBranch: Record<string, FieldOption[]> = {
  'Air Force': RANKS_BY_BRANCH.airforce,
  'Air National Guard': RANKS_BY_BRANCH.airnationalguard,
  Army: RANKS_BY_BRANCH.army,
  'Army National Guard': RANKS_BY_BRANCH.armynationalguard,
  'Coast Guard': RANKS_BY_BRANCH.coastguard,
  'Marine Corps': RANKS_BY_BRANCH.marinecorps,
  Navy: RANKS_BY_BRANCH.navy,
  'Space Force': RANKS_BY_BRANCH.spaceforce,
};

// Status: API expects lowercase ('active', 'reserves', 'separated')
const statusOptions: FieldOption[] = [
  { value: 'active', label: 'Active' },
  { value: 'reserves', label: 'Reserves' },
  { value: 'separated', label: 'Separated' },
];

// Credit type: API expects single letter ('S', 'Q', 'C', 'N')
const creditTypeOptions: FieldOption[] = [
  { value: 'S', label: 'S/SH=Semester Hours' },
  { value: 'Q', label: 'Q=Quarter Hours' },
  { value: 'C', label: 'C=Clock Hours' },
  { value: 'N', label: 'N=Continuing Education Units' },
];

// Level: API expects single letter ('V', 'L', 'U', 'G', 'E', 'D')
const levelOptions: FieldOption[] = [
  { value: 'V', label: 'V=Vocational' },
  { value: 'L', label: 'L=Lower Division Baccalaureate/Associate' },
  { value: 'U', label: 'U=Upper Division Baccalaureate' },
  { value: 'G', label: 'G=Graduate' },
  { value: 'E', label: 'E=Continuing Education' },
  { value: 'D', label: 'D=Development' },
];

const clepDsSTTypeOptions: FieldOption[] = [
  { value: 'clep', label: 'College Level Examination Program (CLEP)' },
  { value: 'dsst', label: 'DANTES Subject Standardized Tests (DSST)' },
];

// CLEP/DSST title options: API expects camelCase enum values
export const jstClepTitles: FieldOption[] = CLEP_TITLES.map((opt: FieldOption) => ({
  value: toCamelCase(opt.label!),
  label: opt.label,
}));

const dlptTypeOptions: FieldOption[] = [{ value: 'dlpt', label: 'Defense Language Proficiency Test (DLPT)' }];

// DLPT language options: API expects camelCase enum values
const DLPT_VALUE_OVERRIDES: Record<string, string> = {
  'DARI-AFGHAN': 'dariafghan',
  'PASHTO-AFGHAN': 'pashtoAfghan',
  'PERSIAN-AFGHAN': 'persianAfghan',
  'PERSIAN-FARSI': 'persianFarsi',
  'HAITIAN CREOLE': 'haitianCreole',
  'SERBIAN/CROATIAN': 'serbianCroatian',
  'CHINESE-AMOY': 'chineseAmoy',
  'CHINESE-MANDARIN': 'chineseMandarin',
  'KURDISH-KURMANJI': 'kurdishKurmanji',
  'KURDISH-SORANI': 'kurdishSorani',
  'ARABIC-ALGERIAN': 'arabicAlgerian',
  'ARABIC-IRAQI': 'arabicIraqi',
  'ARABIC-LEVANTINE': 'arabicLevantine',
  'ARABIC-MSA': 'arabicMsa',
  'ARABIC-SAUDI': 'arabicSaudi',
  'ARABIC-SUDANESE': 'arabicSudanese',
  'ARABIC-YEMENI': 'arabicYemeni',
};

export const jstDlptLanguages: FieldOption[] = DLPT_LANGUAGE_OPTIONS.map((opt: FieldOption) => ({
  value: DLPT_VALUE_OVERRIDES[opt.label!] || opt.label!.toLowerCase(),
  label: opt.label,
}));

const reasonForNoCreditOptions: FieldOption[] = [
  { value: 'notEvaluated', label: '1 - Course has not been evaluated by ACE.' },
  {
    value: 'datesNotRecorded',
    label: "2 - Class attendance dates were not recorded in the service member's record.",
  },
  {
    value: 'notCompletedEvaluationPeriod',
    label: '3 - Course was not completed during the ACE evaluation period.',
  },
  {
    value: 'notEvaluatedAtLocation',
    label: '4 - Course was not evaluated by ACE at this specific location.',
  },
];

// Text rules copied from the Add Education form (LETTERS_NUMBERS_REGEX plus its
// "must contain a letter" check) so a degree or certificate entered on the JST
// passes the same validation as one entered under Education & Skills.
const EDUCATION_TEXT_PATTERN = /^(?=.*[a-zA-Z])[a-zA-Z0-9\s.,'\-()/&;@!?%\\*=]*$/;
const EDUCATION_TEXT_MESSAGE = 'Must contain a letter and use basic punctuation only';

// --- Section Definitions ---

export const jstSections: SectionConfig[] = [
  {
    id: 'personal',
    title: 'Personal Information',
    titleId: 'jst.personal.title',
    singularTitle: 'Personal Information',
    singularTitleId: 'jst.personal.singularTitle',
    buttonLabel: 'Add Personal Information',
    buttonLabelId: 'jst.personal.buttonLabel',
    summaryColumns: ['branch', 'rank', 'status', 'start_date'],
    fields: [
      { name: 'branch', label: 'Branch', labelId: 'jst.fields.branch', type: 'select', options: branchOptions, gridSize: 4, required: true },
      {
        name: 'rank',
        label: 'Current Rank',
        labelId: 'jst.fields.currentRank',
        type: 'select',
        options: rankOptions,
        gridSize: 4,
        required: true,
        dependsOn: { field: 'branch', optionsMap: jstRanksByBranch },
      },
      { name: 'status', label: 'Status', labelId: 'jst.fields.status', type: 'select', options: statusOptions, gridSize: 4, required: true },
      { name: 'start_date', label: 'Start Date', labelId: 'jst.fields.startDate', type: 'date', gridSize: 4, required: true },
      {
        name: 'end_date',
        label: 'End Date',
        labelId: 'jst.fields.endDate',
        type: 'date',
        gridSize: 4,
        required: true,
        // End date applies only to separated service members; hide for active and reserves.
        hideIfFieldValue: { field: 'status', value: ['active', 'reserves'] },
      },
    ],
  },
  {
    id: 'military_courses',
    title: 'Military Courses',
    titleId: 'jst.military_courses.title',
    singularTitle: 'Add Military Course',
    singularTitleId: 'jst.military_courses.singularTitle',
    buttonLabel: 'Add Military Courses',
    buttonLabelId: 'jst.military_courses.buttonLabel',
    summaryColumns: ['military_course_id', 'course_title', 'start_date', 'end_date'],
    fields: [
      {
        name: 'course_title',
        label: 'Course Title/Subject',
        labelId: 'jst.fields.courseTitle',
        type: 'text',
        gridSize: 6,
        maxLength: 100,
        required: true,
        pattern: /^[a-zA-Z0-9\s.,\-\/()&']*$/,
        patternMessage: 'Invalid characters',
      },
      {
        name: 'military_course_id',
        label: 'Military Course ID',
        labelId: 'jst.fields.militaryCourseId',
        type: 'text',
        gridSize: 3,
        maxLength: 20,
        required: false,
        pattern: /^[a-zA-Z0-9\-]*$/,
        patternMessage: 'Only letters, numbers, and hyphens allowed',
      },
      {
        name: 'ace_identifier',
        label: 'ACE Identifier/Exhibit Number',
        labelId: 'jst.fields.aceIdentifier',
        type: 'text',
        gridSize: 3,
        maxLength: 30,
        required: false,
        pattern: /^[a-zA-Z0-9\-\/]*$/,
        patternMessage: 'Only letters, numbers, hyphens and slashes allowed',
      },
      {
        name: 'institution_provider',
        label: 'Institution/Provider',
        labelId: 'jst.fields.institutionProvider',
        type: 'text',
        gridSize: 6,
        groupLabel: 'Location',
      },
      { name: 'city', label: 'City', labelId: 'jst.fields.city', type: 'text', gridSize: 3, required: false, groupLabel: 'Location' },
      { name: 'state', label: 'State', labelId: 'jst.fields.state', type: 'state-select', gridSize: 3, required: false, groupLabel: 'Location' },
      {
        name: 'zipcode',
        label: 'Zip',
        labelId: 'jst.fields.zip',
        type: 'text',
        gridSize: 3,
        required: false,
        groupLabel: 'Location',
        maxLength: 5,
        minLength: 5,
        pattern: /^\d{0,5}$/,
        patternMessage: 'Must be a 5 digit zip code',
      },
      {
        name: 'country',
        label: 'Country',
        labelId: 'jst.fields.country',
        type: 'country-select',
        gridSize: 3,
        required: false,
        groupLabel: 'Location',
      },
      { name: 'start_date', label: 'Start Date', labelId: 'jst.fields.startDate', type: 'date', gridSize: 3, required: true },
      {
        name: 'end_date',
        label: 'End Date',
        labelId: 'jst.fields.endDate',
        type: 'date',
        gridSize: 3,
        required: true,
        // Hidden while the member is still in the course; shown as "Present" in summaries.
        hideIfFieldValue: { field: 'currently_enrolled', value: 'yes' },
      },
      {
        name: 'currently_enrolled',
        label: 'Currently Enrolled',
        labelId: 'jst.fields.currentlyEnrolled',
        type: 'checkbox',
        gridSize: 3,
      },
      {
        name: 'description',
        label: 'Description',
        labelId: 'jst.fields.description',
        type: 'textarea',
        gridSize: 6,
        required: false,
        autoPopulatesSkillsField: 'parsed_skills',
        maxLength: 2000,
      },
      {
        name: 'credit_area',
        label: 'Credit Area',
        labelId: 'jst.fields.creditArea',
        type: 'button',
        gridSize: 3,
        required: false,
        displayField: 'name',
        subFields: [
          { name: 'name', label: 'Credit Area Name', labelId: 'jst.fields.creditAreaName', type: 'text', gridSize: 3 },
          {
            name: 'ace_credit_recommendation',
            label: 'ACE Credit Recommendation',
            labelId: 'jst.fields.aceCreditRecommendation',
            type: 'text',
            gridSize: 3,
            pattern: /^\d*\.?\d*$/,
            patternMessage: 'Only numbers and decimals allowed',
          },
          {
            name: 'credit_type',
            label: 'Credit Type',
            labelId: 'jst.fields.creditType',
            type: 'select',
            options: creditTypeOptions,
            gridSize: 3,
          },
          { name: 'level', label: 'Level', labelId: 'jst.fields.level', type: 'select', options: levelOptions, gridSize: 3 },
        ],
      },
      {
        name: 'parsed_skills',
        label: 'Please add at least 5 skills that you gained from this experience',
        labelId: 'jst.fields.pleaseAddSkills',
        type: 'skills',
        gridSize: 6,
      },
    ],
  },
  {
    id: 'military_experience',
    title: 'Military Experience',
    titleId: 'jst.military_experience.title',
    singularTitle: 'Add Military Experience',
    singularTitleId: 'jst.military_experience.singularTitle',
    buttonLabel: 'Add Military Experience',
    buttonLabelId: 'jst.military_experience.buttonLabel',
    summaryColumns: ['moc_code', 'experience_title', 'rank', 'start_date', 'end_date'],
    fields: [
      {
        name: 'occupation_id',
        label: 'Occupation ID',
        labelId: 'jst.fields.occupationId',
        type: 'hidden',
      },
      {
        name: 'moc_code',
        label: 'Occupation ID (MOS, AFSC, NEC, Rating, etc)',
        labelId: 'jst.fields.mocCode',
        type: 'text',
        gridSize: 3,
        maxLength: 20,
        pattern: /^[a-zA-Z0-9-]*$/,
        patternMessage: 'Only letters, numbers and hyphens allowed',
      },
      {
        name: 'ace_identifier',
        label: 'ACE Identifier/Exhibit Number',
        labelId: 'jst.fields.aceIdentifier',
        type: 'text',
        gridSize: 3,
        maxLength: 30,
        pattern: /^[a-zA-Z0-9\-\/]*$/,
        patternMessage: 'Only letters, numbers, hyphens and slashes allowed',
      },
      {
        name: 'branch_of_service',
        label: 'Branch',
        labelId: 'jst.fields.branch',
        type: 'select',
        options: militaryExpBranchOptions,
        gridSize: 3,
        required: true,
      },
      {
        name: 'rank',
        label: 'Rank',
        labelId: 'jst.fields.rank',
        type: 'select',
        options: rankOptions,
        gridSize: 3,
        required: true,
        dependsOn: { field: 'branch_of_service', optionsMap: militaryExpRanksByBranch },
      },
      {
        name: 'experience_title',
        label: 'Experience Title',
        labelId: 'jst.fields.experienceTitle',
        type: 'militaryTitleAutocomplete',
        gridSize: 6,
        required: true,
        autoPopulatesSkillsField: 'parsed_skills',
        autoPopulatesOccupationField: 'moc_code',
      },
      {
        name: 'occupation',
        label: 'Occupation',
        labelId: 'jst.fields.occupation',
        type: 'militaryOccupationSelect',
        gridSize: 6,
        required: true,
        mocOccupationsFrom: 'experience_title',
        autoPopulatesSkillsField: 'parsed_skills',
      },
      { name: 'start_date', label: 'Start Date', labelId: 'jst.fields.startDate', type: 'date', gridSize: 3, required: true },
      {
        name: 'end_date',
        label: 'End Date',
        labelId: 'jst.fields.endDate',
        type: 'date',
        gridSize: 3,
        required: true,
        hideIfFieldValue: { field: 'currently_enrolled', value: 'yes' },
      },
      {
        name: 'currently_enrolled',
        label: 'Currently Serving',
        labelId: 'jst.fields.currentlyServing',
        type: 'checkbox',
        gridSize: 3,
      },
      {
        name: 'description',
        label: 'Description',
        labelId: 'jst.fields.description',
        type: 'textarea',
        gridSize: 6,
        autoPopulatesSkillsField: 'parsed_skills',
        maxLength: 2000,
        pattern: /^[a-zA-Z0-9\s.,\-\/()&']*$/,
        patternMessage: 'Invalid characters',
      },
      {
        name: 'credit_area',
        label: 'Credit Area',
        labelId: 'jst.fields.creditArea',
        type: 'button',
        gridSize: 3,
        displayField: 'name',
        subFields: [
          { name: 'name', label: 'Credit Area Name', labelId: 'jst.fields.creditAreaName', type: 'text', gridSize: 3 },
          {
            name: 'ace_credit_recommendation',
            label: 'ACE Credit Recommendation',
            labelId: 'jst.fields.aceCreditRecommendation',
            type: 'text',
            gridSize: 3,
            pattern: /^\d*\.?\d*$/,
            patternMessage: 'Only numbers and decimals allowed',
          },
          {
            name: 'credit_type',
            label: 'Credit Type',
            labelId: 'jst.fields.creditType',
            type: 'select',
            options: creditTypeOptions,
            gridSize: 3,
          },
          { name: 'level', label: 'Level', labelId: 'jst.fields.level', type: 'select', options: levelOptions, gridSize: 3 },
        ],
      },
      {
        name: 'parsed_skills',
        label: 'Please add at least 5 skills that you gained from this experience',
        labelId: 'jst.fields.pleaseAddSkills',
        type: 'skills',
        gridSize: 6,
      },
    ],
  },
  {
    id: 'college_level_test_scores',
    title: 'College Level Test Scores',
    titleId: 'jst.college_level_test_scores.title',
    subtitle: `Exams taken after 31 October 2011 may have recommended college credit via the ACE National Guide.\nGo to http://www2.acenet.edu/credit/?fuseaction=browse.getOrganizationDetail&FICE=190163 and look for your exam`,
    subtitleId: 'jst.college_level_test_scores.subtitle',
    subSections: [
      {
        id: 'clep_and_dsst_test_scores',
        subtitle: 'College Level Examination Program (CLEP) & DANTES Subject Standardized Tests (DSST)',
        subtitleId: 'jst.clep_and_dsst_test_scores.subtitle',
        singularTitle: 'CLEP or DSST Scores',
        singularTitleId: 'jst.clep_and_dsst_test_scores.singularTitle',
        buttonLabel: 'Add CLEP or DSST Scores',
        buttonLabelId: 'jst.clep_and_dsst_test_scores.buttonLabel',
        summaryColumns: ['type', 'title', 'date_taken', 'students_score'],
        fields: [
          { name: 'type', label: 'Type', labelId: 'jst.fields.type', type: 'select', options: clepDsSTTypeOptions, gridSize: 4, required: true },
          { name: 'date_taken', label: 'Date Taken', labelId: 'jst.fields.dateTaken', type: 'date', gridSize: 4, required: true },
          {
            name: 'title',
            label: 'Title/Subject',
            labelId: 'jst.fields.titleSubject',
            type: 'select',
            options: jstClepTitles,
            gridSize: 4,
            required: true,
          },
          {
            name: 'recommended_hours',
            label: 'Recommended Hours',
            labelId: 'jst.fields.recommendedHours',
            type: 'text',
            gridSize: 3,
            pattern: /^\d*\.?\d*$/,
            patternMessage: 'Only numbers and decimals allowed',
          },
          {
            name: 'score_required_ace',
            label: 'Score Required by ACE',
            labelId: 'jst.fields.scoreRequiredByAce',
            type: 'text',
            gridSize: 3,
            pattern: /^\d*\.?\d*$/,
            patternMessage: 'Only numbers and decimals allowed',
          },
          {
            name: 'students_score',
            label: "Student's Score",
            labelId: 'jst.fields.studentsScore',
            type: 'text',
            gridSize: 3,
            required: true,
            pattern: /^\d*\.?\d*$/,
            patternMessage: 'Only numbers and decimals allowed',
          },
          {
            name: 'subscore_1',
            label: 'Sub Score 1',
            labelId: 'jst.fields.subScore1',
            type: 'text',
            gridSize: 4,
            pattern: /^\d*\.?\d*$/,
            patternMessage: 'Only numbers and decimals allowed',
          },
          {
            name: 'subscore_2',
            label: 'Sub Score 2',
            labelId: 'jst.fields.subScore2',
            type: 'text',
            gridSize: 4,
            pattern: /^\d*\.?\d*$/,
            patternMessage: 'Only numbers and decimals allowed',
          },
          {
            name: 'verbal_score',
            label: 'Verbal Score',
            labelId: 'jst.fields.verbalScore',
            type: 'text',
            gridSize: 4,
            pattern: /^\d*\.?\d*$/,
            patternMessage: 'Only numbers and decimals allowed',
          },
        ],
      },
      {
        id: 'dlpt_test_scores',
        subtitle: 'Defense Language Proficiency Test (DLPT)',
        subtitleId: 'jst.dlpt_test_scores.subtitle',
        singularTitle: 'DLPT Scores',
        singularTitleId: 'jst.dlpt_test_scores.singularTitle',
        buttonLabel: 'Add DLPT Scores',
        buttonLabelId: 'jst.dlpt_test_scores.buttonLabel',
        summaryColumns: ['type', 'title', 'date_taken', 'students_score'],
        defaultValues: { type: 'dlpt' },
        fields: [
          { name: 'date_taken', label: 'Date Taken', labelId: 'jst.fields.dateTaken', type: 'date', gridSize: 3, required: true },
          {
            name: 'title',
            label: 'Title/Subject',
            labelId: 'jst.fields.titleSubject',
            type: 'select',
            options: jstDlptLanguages,
            gridSize: 3,
            required: true,
          },
          {
            name: 'students_score',
            label: "Student's Score",
            labelId: 'jst.fields.studentsScore',
            type: 'text',
            gridSize: 3,
            required: true,
            pattern: /^\d*\.?\d*$/,
            patternMessage: 'Only numbers and decimals allowed',
          },
          {
            name: 'listening_score',
            label: 'Listening',
            labelId: 'jst.fields.listening',
            type: 'text',
            gridSize: 4,
            groupLabel: 'ACE Recommended Credit',
            pattern: /^\d*\.?\d*$/,
            patternMessage: 'Only numbers and decimals allowed',
          },
          {
            name: 'reading_score',
            label: 'Reading',
            labelId: 'jst.fields.reading',
            type: 'text',
            gridSize: 4,
            groupLabel: 'ACE Recommended Credit',
            pattern: /^\d*\.?\d*$/,
            patternMessage: 'Only numbers and decimals allowed',
          },
          {
            name: 'speaking_score',
            label: 'Speaking',
            labelId: 'jst.fields.speaking',
            type: 'text',
            gridSize: 4,
            groupLabel: 'ACE Recommended Credit',
            pattern: /^\d*\.?\d*$/,
            patternMessage: 'Only numbers and decimals allowed',
          },
        ],
      },
    ],
  },
  {
    id: 'other_learning',
    title: 'Other Learning Experiences',
    titleId: 'jst.other_learning.title',
    singularTitle: 'Other Learning Experiences',
    singularTitleId: 'jst.other_learning.singularTitle',
    summaryColumns: ['course_id', 'title', 'date_taken', 'institution_provider'],
    subtitle: `This section provides a record of the service member's learning experiences that do not have credit recommended for one or more of the following reasons:

(1) Course has not been evaluated by ACE.
(2) Class attendance dates were not recorded in the service member's record.
(3) Course was not completed during the ACE evaluation period.
(4) Course was not evaluated by ACE at this specific location.`,
    subtitleId: 'jst.other_learning.subtitle',
    buttonLabel: 'Add Other Learning Experiences',
    buttonLabelId: 'jst.other_learning.buttonLabel',
    note: 'Note: If the Provider/Institution is not listed on the JST, please enter the Branch of Service in the Provider/Institution field.',
    noteId: 'jst.other_learning.note',
    fields: [
      { name: 'course_id', label: 'Course ID', labelId: 'jst.fields.courseId', type: 'text', gridSize: 3 },
      { name: 'date_taken', label: 'Date Taken', labelId: 'jst.fields.dateTaken', type: 'date', gridSize: 3, required: true },
      { name: 'title', label: 'Title', labelId: 'jst.fields.title', type: 'text', gridSize: 3, required: true },
      {
        name: 'institution_provider',
        label: 'Institution/Provider',
        labelId: 'jst.fields.institutionProvider',
        type: 'text',
        gridSize: 3,
        groupLabel: 'Location',
      },
      { name: 'city', label: 'City', labelId: 'jst.fields.city', type: 'text', gridSize: 3, groupLabel: 'Location' },
      { name: 'state', label: 'State', labelId: 'jst.fields.state', type: 'state-select', gridSize: 3, groupLabel: 'Location' },
      {
        name: 'zipcode',
        label: 'Zip',
        labelId: 'jst.fields.zip',
        type: 'text',
        gridSize: 3,
        groupLabel: 'Location',
        maxLength: 5,
        minLength: 5,
        pattern: /^\d{0,5}$/,
        patternMessage: 'Must be a 5 digit zip code',
      },
      {
        name: 'country',
        label: 'Country',
        labelId: 'jst.fields.country',
        type: 'country-select',
        gridSize: 3,
        required: false,
        groupLabel: 'Location',
      },
      {
        name: 'reason_no_credit_recommendation',
        label: 'Reason for No Credit Recommendation',
        labelId: 'jst.fields.reasonNoCreditRecommendation',
        type: 'select',
        options: reasonForNoCreditOptions,
        gridSize: 6,
      },
      {
        name: 'parsed_skills',
        label: 'Please add at least 5 skills that you gained from this experience',
        labelId: 'jst.fields.pleaseAddSkills',
        type: 'skills',
        gridSize: 6,
      },
    ],
  },
  {
    id: 'academic_courses',
    title: 'Academic Course Table',
    titleId: 'jst.academic_courses.title',
    singularTitle: 'Academic Course',
    singularTitleId: 'jst.academic_courses.singularTitle',
    buttonLabel: 'Add Academic Course',
    buttonLabelId: 'jst.academic_courses.buttonLabel',
    summaryColumns: ['course_number', 'title', 'start_date', 'end_date'],
    fields: [
      { name: 'course_number', label: 'Course Number', labelId: 'jst.fields.courseNumber', type: 'text', gridSize: 3 },
      { name: 'title', label: 'Title', labelId: 'jst.fields.title', type: 'text', gridSize: 3, required: true },
      {
        name: 'currently_enrolled',
        label: 'Currently Enrolled',
        labelId: 'jst.fields.currentlyEnrolled',
        type: 'select',
        options: [
          { value: 'true', label: 'Yes' },
          { value: 'false', label: 'No' },
        ],
        gridSize: 3,
      },
      { name: 'start_date', label: 'Start Date', labelId: 'jst.fields.startDate', type: 'date', gridSize: 3, required: true },
      {
        name: 'end_date',
        label: 'End Date',
        labelId: 'jst.fields.endDate',
        type: 'date',
        gridSize: 3,
        required: true,
        hideIfFieldValue: { field: 'currently_enrolled', value: 'true' },
      },
      {
        name: 'credits',
        label: 'Credits',
        labelId: 'jst.fields.credits',
        type: 'text',
        gridSize: 3,
        pattern: /^\d*\.?\d*$/,
        patternMessage: 'Only numbers and decimals allowed',
      },
      { name: 'level', label: 'Level', labelId: 'jst.fields.level', type: 'select', options: levelOptions, gridSize: 3 },
      {
        name: 'grade',
        label: 'Grade',
        labelId: 'jst.fields.grade',
        type: 'text',
        gridSize: 3,
        hideIfFieldValue: { field: 'currently_enrolled', value: 'true' },
      },
      { name: 'institution_name', label: 'Name', labelId: 'jst.fields.institutionName', type: 'text', gridSize: 4, groupLabel: 'Institution' },
      { name: 'institution_address', label: 'Address', labelId: 'jst.fields.address', type: 'text', gridSize: 8, groupLabel: 'Institution' },
      { name: 'institution_city', label: 'City', labelId: 'jst.fields.city', type: 'text', gridSize: 3, groupLabel: 'Institution' },
      { name: 'institution_state', label: 'State', labelId: 'jst.fields.state', type: 'state-select', gridSize: 3, groupLabel: 'Institution' },
      {
        name: 'institution_zipcode',
        label: 'Zip',
        labelId: 'jst.fields.zip',
        type: 'text',
        gridSize: 3,
        groupLabel: 'Institution',
        maxLength: 5,
        minLength: 5,
        pattern: /^\d{0,5}$/,
        patternMessage: 'Must be a 5 digit zip code',
      },
      {
        name: 'parsed_skills',
        label: 'Please add at least 5 skills that you gained from this experience',
        labelId: 'jst.fields.pleaseAddSkills',
        type: 'skills',
        gridSize: 6,
      },
    ],
  },
  {
    id: 'academic_degrees_certificates',
    title: 'Academic Degrees/Certificates',
    titleId: 'jst.academic_degrees_certificates.title',
    singularTitle: 'Academic Degree/Certificate',
    singularTitleId: 'jst.academic_degrees_certificates.singularTitle',
    buttonLabel: 'Add Academic Degree/Certificate',
    buttonLabelId: 'jst.academic_degrees_certificates.buttonLabel',
    summaryColumns: ['education_experience_credential', 'name', 'start_date', 'end_date'],
    fields: [
      {
        name: 'education_experience_credential',
        label: 'Education Experience/Credentials',
        labelId: 'jst.fields.educationExperienceCredentials',
        type: 'select',
        options: EDUCATION_TYPE_OPTIONS,
        gridSize: 4,
        required: true,
      },
      {
        // Free text, not a dropdown: the fixed institution-type list could not
        // represent the school the user actually attended. Required, with the same
        // character rules as the Add Education provider field, so an entry made
        // here is accepted on the Education & Skills side too.
        name: 'institution_provider',
        label: 'Institution/Provider',
        labelId: 'jst.fields.institutionProvider',
        type: 'text',
        gridSize: 4,
        required: true,
        maxLength: 100,
        pattern: EDUCATION_TEXT_PATTERN,
        patternMessage: EDUCATION_TEXT_MESSAGE,
      },
      {
        name: 'name',
        label: 'Name of experience/Credential or Major',
        labelId: 'jst.fields.nameOfExperienceCredential',
        type: 'text',
        gridSize: 4,
        required: true,
        maxLength: 100,
        pattern: EDUCATION_TEXT_PATTERN,
        patternMessage: EDUCATION_TEXT_MESSAGE,
      },
      {
        name: 'skills_gained',
        label: 'Please add at least 5 skills that you gained from this experience',
        labelId: 'jst.fields.pleaseAddSkills',
        type: 'skills',
        gridSize: 3,
      },
      {
        name: 'currently_enrolled',
        label: 'Currently Enrolled',
        labelId: 'jst.fields.currentlyEnrolled',
        type: 'select',
        options: [
          { value: 'true', label: 'Yes' },
          { value: 'false', label: 'No' },
        ],
        gridSize: 3,
      },
      { name: 'start_date', label: 'Start Date', labelId: 'jst.fields.startDate', type: 'date', gridSize: 3, required: true },
      {
        name: 'end_date',
        label: 'End Date',
        labelId: 'jst.fields.endDate',
        type: 'date',
        gridSize: 3,
        required: true,
        hideIfFieldValue: { field: 'currently_enrolled', value: 'true' },
      },
    ],
  },
];
